import { AxiosError, AxiosInstance, AxiosHeaders, InternalAxiosRequestConfig } from 'axios';
import { authDebugEnabled, logAuthDebug } from '../auth/roleHeaders';
import {
  localStorageActiveRoleAllowsStudentTutorApis,
  requiresAiTutorStudentScope,
} from '../auth/tutorStudentApiScope';
import {
  AuthSessionExpiredHandledError,
  isLogoutRedirectInProgress,
  isUnauthorizedApiResponse,
  refreshAccessTokenSingleFlight,
  rejectWithAuthSessionExpired,
} from '../utils/authSession';
import { getAccessToken } from '../utils/token';
import { getAuthHeaders } from '../utils/apiHeaders';

function stripAuthorizationHeader(headers: InternalAxiosRequestConfig['headers']): void {
  if (!headers) return;
  if (headers instanceof AxiosHeaders) {
    headers.delete('Authorization');
    return;
  }
  const h = headers as Record<string, unknown>;
  delete h.Authorization;
  delete h.authorization;
}

function requestSkipsAuthRedirect(config: InternalAxiosRequestConfig): boolean {
  const headers = config.headers as Record<string, unknown> | undefined;
  return !!(
    headers &&
    (headers['x-skip-auth-redirect'] ||
      headers['X-Skip-Auth-Redirect'] ||
      headers['x-skip-auth-redirect'.toLowerCase()])
  );
}

export const attachAuthInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (isLogoutRedirectInProgress() && !requestSkipsAuthRedirect(config)) {
        return Promise.reject(new AuthSessionExpiredHandledError());
      }

      if (!config.headers) {
        config.headers = new AxiosHeaders();
      }

      // Let the browser set multipart boundary — default JSON Content-Type breaks FormData.
      if (config.data instanceof FormData) {
        if (config.headers instanceof AxiosHeaders) {
          config.headers.delete('Content-Type');
        } else {
          const h = config.headers as Record<string, unknown>;
          delete h['Content-Type'];
          delete h['content-type'];
        }
      }

      const auth = getAuthHeaders();
      // config.headers may be an AxiosHeaders instance or a plain object.
      // Use the AxiosHeaders API when available so headers are set correctly
      // (Object.assign on AxiosHeaders does not populate the internal header map).
      if (config.headers instanceof AxiosHeaders) {
        for (const [k, v] of Object.entries(auth)) {
          if (v != null) config.headers.set(k, String(v));
        }
      } else {
        Object.assign(config.headers, auth);
      }

      const token = getAccessToken();
      if (!token) {
        stripAuthorizationHeader(config.headers);
      }

      const rel = String(config.url || '');
      if (requiresAiTutorStudentScope(rel) && !localStorageActiveRoleAllowsStudentTutorApis()) {
        const role = (() => {
          try {
            return localStorage.getItem('X_ROLE');
          } catch {
            return null;
          }
        })();
        logAuthDebug('axios_blocked_student_tutor_scope', {
          method: config.method,
          url: rel,
          xRole: role,
        });
        // eslint-disable-next-line no-console
        console.warn('[auth] Blocked student-scoped AI Tutor REST call', {
          method: config.method,
          url: rel,
          xRole: role,
        });
        return Promise.reject(
          new Error(
            `[auth] Refusing ${config.method ?? 'REQUEST'} ${rel}: active X_ROLE is not STUDENT (got "${role ?? ''}"). Pick Student on login or use a student-only route.`
          )
        );
      }
      if (import.meta.env.VITE_AUTH_DEBUG === '1' && (rel.includes('/tutor') || rel.includes('v1/tutor'))) {
        logAuthDebug('axios_tutor_out', {
          method: config.method,
          url: rel,
          xRole: (() => {
            try {
              return localStorage.getItem('X_ROLE');
            } catch {
              return null;
            }
          })(),
        });
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (error instanceof AuthSessionExpiredHandledError) {
        return Promise.reject(error);
      }

      if (isLogoutRedirectInProgress()) {
        return rejectWithAuthSessionExpired();
      }

      const originalRequest = (error.config || {}) as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };
      const status = error.response?.status ?? 0;
      const responseData = error.response?.data;

      if (
        isUnauthorizedApiResponse(status, responseData) &&
        !originalRequest.url?.includes('/auth/refresh') &&
        !requestSkipsAuthRedirect(originalRequest)
      ) {
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          const accessToken = await refreshAccessTokenSingleFlight();

          if (!accessToken || isLogoutRedirectInProgress()) {
            return rejectWithAuthSessionExpired();
          }

          const auth = getAuthHeaders();
          const h = originalRequest.headers;
          if (h instanceof AxiosHeaders) {
            h.set('Authorization', `Bearer ${accessToken}`);
            if (auth['x-tenant-id']) h.set('x-tenant-id', auth['x-tenant-id']);
            if (auth['x-user-id']) h.set('x-user-id', auth['x-user-id']);
            if (auth['x-role']) h.set('x-role', auth['x-role']);
            return instance(originalRequest);
          }
          const mergedHeaders: Record<string, string> = {
            ...(typeof h === 'object' && h !== null ? (h as Record<string, string>) : {}),
            ...auth,
            Authorization: `Bearer ${accessToken}`,
          };
          return instance({
            ...originalRequest,
            headers: mergedHeaders,
          });
        }

        return rejectWithAuthSessionExpired();
      }

      if (error.response?.status === 403) {
        const rel = String(originalRequest?.url || '');
        const role = (() => {
          try {
            return localStorage.getItem('X_ROLE');
          } catch {
            return null;
          }
        })();
        console.warn('[auth] Forbidden (403)', { url: rel, method: originalRequest?.method, xRole: role });
        if (authDebugEnabled()) {
          logAuthDebug('axios_403', {
            url: rel,
            method: originalRequest?.method,
            xRole: role,
            detail: error.response?.data,
          });
        }
      }

      return Promise.reject(error);
    }
  );
};
